//path: src\features\sceneAssets\sceneAssetsReducer.ts

import {
  SET_SCENE_ASSETS_ARRAY,
  TOGGLE_LOCK,
  TOGGLE_VISIBILITY,
  SET_SELECTED_ASSET,
  TOGGLE_ASSET_MUTE,
  UPDATE_ASSET_TIMES,
  ADD_SCENE_ASSET,
  PATCH_SCENE_ASSET,
  REPLACE_SCENE_ASSET,
  REMOVE_SCENE_ASSETS,
  SceneAssetsArrayActionTypes
} from './sceneAssetsActions';
import { SET_SELECTED_TRIGGER_ID } from '@/features/editorSelection/editorSelectionActions';
import { AssetInstance } from '@/types/assetTypes';

interface SceneAssetsArrayState {
  sceneAssetsArray: AssetInstance[] | [];
}

const initialState: SceneAssetsArrayState = {
  sceneAssetsArray: []
};

const sceneAssetsArrayReducer = (
  state: SceneAssetsArrayState = initialState,
  action: SceneAssetsArrayActionTypes
): SceneAssetsArrayState => {
  // A trigger is itself an asset instance, so selecting one deselects any normal asset.
  if ((action.type as string) === SET_SELECTED_TRIGGER_ID && (action as { payload?: unknown }).payload) {
    if (!state.sceneAssetsArray.some((a: AssetInstance) => a.isSelected)) return state;
    return {
      ...state,
      sceneAssetsArray: state.sceneAssetsArray.map((a: AssetInstance) =>
        a.isSelected ? { ...a, isSelected: false } : a
      ),
    };
  }
  switch (action.type) {
    case SET_SCENE_ASSETS_ARRAY:
      return {
        ...state,
        sceneAssetsArray: action.payload
      };
    case TOGGLE_LOCK: {
      const { assetIds, targetLockState } = action.payload;
      return {
        ...state,
        sceneAssetsArray: state.sceneAssetsArray.map((asset) =>
          assetIds.includes(asset.instanceId)
            ? { ...asset, isLocked: targetLockState }
            : asset
        )
      };
    }
    case SET_SELECTED_ASSET:
      return {
        ...state,
        sceneAssetsArray: state.sceneAssetsArray.map(
          (asset: AssetInstance) => ({
            ...asset,
            isSelected:
              action.payload.instanceId !== null &&
              asset.instanceId === action.payload.instanceId
          })
        )
      };
      
    case TOGGLE_VISIBILITY: {
      const { assetIds, targetVisibleState } = action.payload;
      return {
        ...state,
        sceneAssetsArray: state.sceneAssetsArray.map((asset) =>
          assetIds.includes(asset.instanceId)
            ? { ...asset, isVisible: targetVisibleState }
            : asset
        )
      };
    }
    case TOGGLE_ASSET_MUTE:
      if (typeof action.payload === 'string') {
        // Handle single asset toggle
        return {
          ...state,
          sceneAssetsArray: state.sceneAssetsArray.map((asset) =>
            asset.instanceId === action.payload
              ? { ...asset, isMuted: !asset.isMuted }
              : asset
          )
        };
      } else {
        // Handle category/batch toggle
        const { assetIds, targetMutedState } = action.payload;
        return {
          ...state,
          sceneAssetsArray: state.sceneAssetsArray.map((asset) =>
            assetIds.includes(asset.instanceId)
              ? { ...asset, isMuted: targetMutedState ?? !asset.isMuted }
              : asset
          )
        };
      }
    case ADD_SCENE_ASSET: {
      const { asset, at } = action.payload;
      if (state.sceneAssetsArray.some((a: AssetInstance) => a.instanceId === asset.instanceId)) return state;
      const next = [...state.sceneAssetsArray];
      next.splice(at === undefined ? next.length : Math.max(0, Math.min(at, next.length)), 0, asset);
      return { ...state, sceneAssetsArray: next };
    }
    case PATCH_SCENE_ASSET: {
      const { instanceId, changes } = action.payload;
      if (!state.sceneAssetsArray.some((a: AssetInstance) => a.instanceId === instanceId)) return state;
      return {
        ...state,
        sceneAssetsArray: state.sceneAssetsArray.map((a: AssetInstance) => (a.instanceId === instanceId ? { ...a, ...changes } : a))
      };
    }
    case REPLACE_SCENE_ASSET: {
      const { instanceId, asset } = action.payload;
      if (state.sceneAssetsArray.some((a: AssetInstance) => a.instanceId === asset.instanceId)) return state;
      return {
        ...state,
        sceneAssetsArray: state.sceneAssetsArray.map((a: AssetInstance) => (a.instanceId === instanceId ? asset : a))
      };
    }
    case REMOVE_SCENE_ASSETS: {
      const gone = new Set(action.payload);
      if (!state.sceneAssetsArray.some((a: AssetInstance) => gone.has(a.instanceId))) return state;
      return { ...state, sceneAssetsArray: state.sceneAssetsArray.filter((a: AssetInstance) => !gone.has(a.instanceId)) };
    }
    case UPDATE_ASSET_TIMES:
      return {
        ...state,
        sceneAssetsArray: state.sceneAssetsArray.map(
          (asset: AssetInstance) =>
            asset.instanceId === action.payload.assetInstanceId
              ? {
                  ...asset,
                  instanceBeginTime: action.payload.beginTime,
                  instanceEndTime: action.payload.endTime
                }
              : asset
        )
      };
    default:
      return state;
  }
};

export default sceneAssetsArrayReducer;
